import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { Label } from "@/components/ui/label"
import { missionProductParamNames } from "@/lib/mission-product-param-names"
import type {
  CatalogPickerVariant,
  MissionProductFormData,
} from "@/types/missions"
import { missionProductPinState } from "@/types/missions"

/**
 * ⚠️ TOP-LEVEL, not nested under `mission_products_attributes` — and this
 * shipped wrong.
 *
 * It was `mission[mission_products_attributes][0][attach_variant_ids][]` while the controller reads
 * `params.dig(:mission, :attach_variant_ids)`. The mismatch is silent:
 * `Array(nil)` → `[]` → **every attach landed unpinned**, with no error and
 * nothing red.
 *
 * The wire has TWO shapes on purpose and this is the seam between them:
 *
 *   TOP-LEVEL   attach params — `mission[attach_product_id]`,
 *               `mission[attach_variant_ids][]`. Never permitted into the
 *               model; routed through `MissionProducts::Attacher`, which is
 *               what makes `product_id` write-once (D2).
 *   NESTED      flags read via `params.dig(:mission,
 *               :mission_products_attributes, …)` — `detach_product`,
 *               `clear_variant_pin`, `remove_photo`.
 *
 * Both siblings in this file are nested flags, so a nested attach param read
 * as correct at a glance. Extracted to a constant so the two call sites cannot
 * drift from each other again.
 */
// OKS-1040 — the names now come from `missionProductParamNames(index)`: row 0
// keeps exactly the top-level shape above, rows ≥ 1 post under their own row.

/**
 * OKS-933 — the variant pin (UX Spec §5).
 *
 * ⚠️ §5 is a STATE TABLE keyed on the tuple (`product_id`, `variant_pinned`,
 * live members) — **never on where a block sits on the page.** Its first draft
 * specified placement ("below the product summary in S2"), which was true for
 * the arm that existed then and silently wrong for an arm created later by
 * someone else. A spec keyed on location absorbs new states invisibly; one
 * keyed on the tuple exposes them as a row with no rendering.
 *
 * The states, and the two that are one boolean apart:
 *
 *   unpinned         no restriction. Also a product-deleted row that never had
 *                    one — deliberately indistinguishable from a legacy row,
 *                    because there is no loss to report.
 *   pinned           attached, ≥1 live member. INTACT even when some members
 *                    are archived: the sweep only archives, so those rows
 *                    survive and render checked + disabled + the chip.
 *   pin-orphaned     attached, ZERO members. A manual variant was hard-deleted
 *                    by `Products::ManualWriter` and cascaded away.
 *   detached-orphan  product GONE (store disconnect / brand teardown). Same
 *                    boolean and member count as pin-orphaned — differs ONLY by
 *                    `product_id`. Renders in S3 (S3b), not here.
 *
 * ⚠️ A branch on "pinned with no members" that skips `product_id` renders S2's
 * summary card over a null product. `missionProductPinState()` exists so that
 * discrimination happens in one place.
 */
export interface MissionProductVariantPinProps {
  product: MissionProductFormData
  /** Variants of the attached catalog product, or [] when it is gone. */
  variants: CatalogPickerVariant[]
  /** OKS-1040 — the offered row this pin belongs to (0 = legacy names). */
  index?: number
  /** Ids that are archived — render checked+disabled, never newly pinnable. */
  archivedVariantIds?: number[]
}

export function MissionProductVariantPin({
  product,
  variants,
  archivedVariantIds = [],
  index = 0,
}: MissionProductVariantPinProps) {
  const { t } = useTranslation()
  // OKS-1040 — row-aware names; row 0 is byte-identical (see the helper).
  const names = missionProductParamNames(index)
  const state = missionProductPinState(product)
  const orphaned = state === "pin-orphaned"

  // Expanded by default when the pin is orphaned — the brand needs to see the
  // warning without hunting for it (UX §5).
  const [open, setOpen] = useState(orphaned || state === "pinned")
  /**
   * ⚠️ "The operator touched the pin" — NOT "this form contains pin inputs".
   *
   * The checkboxes post on EVERY save, so the controller cannot simply consume
   * `attach_variant_ids` whenever they are present: an unrelated save (a title
   * edit) would rewrite the pin from whatever the DOM happened to hold. The
   * old guard avoided that by only reading them alongside an `attach_product_id`
   * — which made the pin READ-ONLY on an already-attached mission, and on a
   * mission with collabs also undetachable, so a wrong pin was permanent.
   *
   * This flag is the discriminator both properties need: absent on an
   * untouched form (pin preserved), present the moment a box is toggled (pin
   * editable). Widening the guard instead would trade a read-only pin for one
   * that silently rewrites on every save — strictly worse and far harder to
   * notice.
   */
  const [touched, setTouched] = useState(false)

  return (
    <div className="flex flex-col gap-2" data-testid="mission-product-pin">
      {/* ⚠️ Hidden empty-string sentinel. Rails drops an ABSENT array, so
          "the operator unchecked everything" would arrive as "no change" and
          the pin would silently survive. The sentinel makes the empty set
          expressible on the wire. */}
      <input type="hidden" name={names.attachVariantIds} value="" />
      {touched && (
        <input
          type="hidden"
          name={names.attachVariantIdsTouched}
          value="1"
          data-testid="mission-product-pin-touched"
        />
      )}

      {orphaned && (
        /* ⚠️ `default` variant, never `destructive`. The brand removed a
           variant from their own catalogue — an ordinary, correct action. The
           UI must not treat it as damage. */
        <Alert data-testid="mission-product-pin-orphaned">
          <AlertTitle>
            {t("missions.form.product.pin.orphaned_title")}
          </AlertTitle>
          <AlertDescription>
            {/* ⚠️ Says what is no longer RECORDED, never what will happen. The
                pin has no enforcement consumer until the next ticket, so
                promising behaviour here would describe a feature that does not
                exist — and would read as a bug the day enforcement lands. */}
            {t("missions.form.product.pin.orphaned_body")}
          </AlertDescription>
        </Alert>
      )}

      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger
          render={
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-fit px-0"
              data-testid="mission-product-pin-toggle"
            />
          }
        >
          {t("missions.form.product.pin.toggle")}
        </CollapsibleTrigger>

        <CollapsibleContent className="flex flex-col gap-2 pt-2">
          <p className="text-muted-foreground text-xs">
            {t("missions.form.product.pin.helper")}
          </p>

          {variants.map((variant) => {
            const archived = archivedVariantIds.includes(variant.id)
            const checked = product.pinned_variant_ids.includes(variant.id)
            const id = `mission-product-variant-${variant.id}`

            return (
              <div key={variant.id} className="flex items-center gap-2">
                <Checkbox
                  id={id}
                  name={names.attachVariantIds}
                  value={String(variant.id)}
                  defaultChecked={checked}
                  onCheckedChange={() => setTouched(true)}
                  // ⚠️ An ARCHIVED member stays checked and disabled — the pin
                  // is intact, the variant just left the active catalogue. You
                  // cannot newly pin one (the server resolves through
                  // `active_listing`), which is a different lifecycle point and
                  // easy to conflate.
                  disabled={archived}
                />
                <Label htmlFor={id} className="flex items-center gap-2 text-sm">
                  <span>{variant.title}</span>
                  {variant.sku && (
                    <span className="text-muted-foreground text-xs">
                      {variant.sku}
                    </span>
                  )}
                  {archived && (
                    <Badge variant="secondary">
                      {t("missions.form.product.no_longer_in_catalog")}
                    </Badge>
                  )}
                </Label>
              </div>
            )
          })}
        </CollapsibleContent>
      </Collapsible>
    </div>
  )
}

/**
 * OKS-933 (D3) — S3b: the product itself is gone, and a restriction was set.
 *
 * ⚠️ Renders in S3, NOT S2. `product_id` is NULL so there is no title, price or
 * thumbnail to summarise — an S2 card here would be invented. The orphaned row
 * genuinely IS an S3 row; it just has one more thing to say.
 */
export function MissionProductDetachedPin({
  index = 0,
}: {
  /** OKS-1040 — the offered row this alert belongs to (0 = legacy names). */
  index?: number
} = {}) {
  const { t } = useTranslation()
  const names = missionProductParamNames(index)

  return (
    <Alert data-testid="mission-product-detached-pin">
      <AlertTitle>{t("missions.form.product.pin.detached_title")}</AlertTitle>
      <AlertDescription className="flex flex-col items-start gap-2">
        <span>{t("missions.form.product.pin.detached_body")}</span>
        {/* ⚠️ A real state transition (`variant_pinned` → false), not a
            dismissal. A UI-only dismiss would hide a live state, and
            `localStorage` is banned anyway. Submits the parent form — same
            shape as the shipped `remove_photo` flag. */}
        <Button
          type="submit"
          name={names.clearVariantPin}
          value="1"
          variant="outline"
          size="sm"
          data-testid="mission-product-clear-pin"
        >
          {t("missions.form.product.pin.clear_restriction")}
        </Button>
      </AlertDescription>
    </Alert>
  )
}

export default MissionProductVariantPin
